import React from 'react'
import { useLocation } from '@reach/router'
import { initializeAndTrack } from 'gatsby-plugin-gdpr-cookies'
import { TranslatedLink } from '../TranslatedLink'
import LinkBtn from '../linkBtn/LinkBtn'
import Modal from '../pages/modal/Modal'
import { isBrowser, getTranslateFunction } from '../../utilities'
import cookieIcon from '../../images/svg/cookie/cookie.svg'
import cookieBlueIcon from '../../images/svg/cookie/cookie-blue.svg'
import arowRight from '../../images/svg/arrow_right.svg'
import CookieToggle from './CookieToggle'
import {
  COOKIE_VISIBILITY_HIDDEN_CLASS,
  COOKIE_VISIBILITY_VISIBLE_CLASS,
} from './constants'
import './CookieBar.scss'

const HIDE_COOKIE_BAR_LS_KEY = 'narative-hide-cookie-bar'
const GOOGLE_TAG_MANAGER_KEY = 'narative-gdpr-google-tagmanager'
const PIXEL_KEY = 'narative-2-gdpr-facebook-pixel'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

const parseStoredValue = (rawValue) => {
  if (rawValue == null || rawValue === '') {
    return null
  }

  if (rawValue === 'true') {
    return true
  }

  if (rawValue === 'false') {
    return false
  }

  try {
    const parsedValue = JSON.parse(rawValue)
    return typeof parsedValue === 'boolean' ? parsedValue : null
  } catch (error) {
    return null
  }
}

const getStoredCookieValue = (key) => {
  if (!isBrowser()) {
    return null
  }

  const match = document.cookie.match(new RegExp(`(?:^|; )${key}=([^;]*)`))
  return match ? parseStoredValue(decodeURIComponent(match[1])) : null
}

const getStoredValue = (key) => {
  if (!isBrowser()) {
    return null
  }

  let localStorageValue = null

  try {
    localStorageValue = parseStoredValue(window.localStorage.getItem(key))
  } catch (error) {
    localStorageValue = null
  }

  if (localStorageValue != null) {
    return localStorageValue
  }

  return getStoredCookieValue(key)
}

const setStoredValue = (key, value) => {
  if (!isBrowser()) {
    return
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    // Ignore storage write failures and fall back to cookies.
  }

  document.cookie = `${key}=${value};path=/;max-age=${COOKIE_MAX_AGE};SameSite=Lax`
}

const syncCookieBarVisibility = (isHidden) => {
  if (!isBrowser()) {
    return
  }

  document.documentElement.classList.toggle(
    COOKIE_VISIBILITY_HIDDEN_CLASS,
    isHidden
  )
  document.documentElement.classList.toggle(
    COOKIE_VISIBILITY_VISIBLE_CLASS,
    !isHidden
  )
}

const enableGtag = () => {
  if (!isBrowser()) {
    return
  }

  window.dataLayer = window.dataLayer || []

  function gtag() {
    window.dataLayer.push(arguments)
  }

  gtag('consent', 'update', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted',
  })
}

const loadGtag = () => {
  if (!isBrowser()) {
    return
  }

  window.dataLayer = window.dataLayer || []

  function gtag() {
    window.dataLayer.push(arguments)
  }

  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  })
}

const getInitialCookieManagerHidden = () =>
  getStoredValue(HIDE_COOKIE_BAR_LS_KEY) === true

const getInitialTagManagerValue = () => {
  const storedValue = getStoredValue(GOOGLE_TAG_MANAGER_KEY)
  return storedValue == null ? true : storedValue
}

const splitCookieText = (value = '') => {
  const match = value.match(/<b>(.*?)<\/b>\s*-\s*([\s\S]*)/i)

  if (!match) {
    return {
      title: '',
      description: value,
    }
  }

  return {
    title: match[1],
    description: match[2],
  }
}

const CookieBar = ({ lang = 'sk' }) => {
  const slug =
    lang === 'en'
      ? '/en/privacy-policy'
      : '/zasady-spracuvania-osobnych-udajov/'
  const t = getTranslateFunction(lang)
  const location = useLocation()
  const modalRef = React.useRef(null)
  const [cookieManagerHidden, setCookieManagerHidden] = React.useState(
    getInitialCookieManagerHidden
  )
  const [tagManagerValue, setTagManagerValue] = React.useState(
    getInitialTagManagerValue
  )
  const [preferencesReady, setPreferencesReady] = React.useState(false)
  const necessaryCopy = splitCookieText(t('cookieBar.NesesseryText'))
  const optionalCopy = splitCookieText(t('cookieBar.OptionalText'))

  const enable = React.useCallback(() => {
    if (tagManagerValue) {
      enableGtag()
    } else {
      loadGtag()
    }

    setStoredValue(HIDE_COOKIE_BAR_LS_KEY, true)
    setStoredValue(GOOGLE_TAG_MANAGER_KEY, tagManagerValue)
    setStoredValue(PIXEL_KEY, tagManagerValue)
    syncCookieBarVisibility(true)
    setCookieManagerHidden(true)
  }, [tagManagerValue])

  const onClickChild = React.useCallback(() => {
    modalRef.current?.showModal()
  }, [])

  const onSubmitCookiesForm = React.useCallback(() => {
    enable()
  }, [enable])

  React.useEffect(() => {
    if (!isBrowser()) {
      return undefined
    }

    initializeAndTrack(location)
  }, [location])

  React.useEffect(() => {
    if (!isBrowser()) {
      return undefined
    }

    const storedCookieConsent = getStoredValue(HIDE_COOKIE_BAR_LS_KEY)
    const storedTagManagerConsent = getStoredValue(GOOGLE_TAG_MANAGER_KEY)

    if (storedCookieConsent != null) {
      setCookieManagerHidden(storedCookieConsent)
    }

    if (storedTagManagerConsent != null) {
      setTagManagerValue(storedTagManagerConsent)
    }

    if (storedCookieConsent === true && storedTagManagerConsent !== false) {
      enableGtag()
    } else {
      loadGtag()
    }

    setPreferencesReady(true)

    const handleShowCookieBar = () => {
      modalRef.current?.showModal()
    }

    document.addEventListener('showCookieBar', handleShowCookieBar)

    return () => {
      document.removeEventListener('showCookieBar', handleShowCookieBar)
    }
  }, [])

  React.useEffect(() => {
    if (!preferencesReady) {
      return
    }

    syncCookieBarVisibility(cookieManagerHidden)
  }, [cookieManagerHidden, preferencesReady])

  return (
    <>
      {!cookieManagerHidden && (
        <div className="cookie-bar">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-12 col-lg cookie-bar__main">
                <div className="cookie-bar__content">
                  <img
                    className="cookie-bar__icon"
                    src={cookieBlueIcon}
                    alt=""
                    aria-hidden="true"
                  />
                  <div className="cookie-bar__text">
                    <div className="cookie-bar__title">
                      {t('cookieBar.Title')}
                    </div>
                    <div className="cookie-bar__description">
                      <span
                        dangerouslySetInnerHTML={{
                          __html: t('cookieBar.Text1'),
                        }}
                      ></span>
                      <TranslatedLink lang={lang} to={slug}>
                        {t('cookieBar.Text2')}
                      </TranslatedLink>
                      .
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-lg-auto pt-2 pt-lg-0 d-flex align-items-center cookie-bar__actions">
                <LinkBtn
                  className="loaded accept"
                  showBg={true}
                  handleClick={enable}
                  color={'blue'}
                >
                  <span className="pl-4 pr-4">
                    {t('cookieBar.BtnSubmit')}
                    <img
                      src={arowRight}
                      alt=""
                      className="fix-arrow-cookiebar"
                    />
                  </span>
                </LinkBtn>
                <LinkBtn className="loaded  text-1" color="silver">
                  <span onClick={onClickChild}>{t('cookieBar.BtnEdit')}</span>
                </LinkBtn>
              </div>
            </div>
          </div>
        </div>
      )}
      <Modal
        className="cookie-bar-modal"
        closeBtn={t('cookieBar.Cancel')}
        submitBtn={t('cookieBar.SaveChoices')}
        submitButtonClassName="loaded accept cookie-bar-modal__submit"
        submitButtonContent={
          <span className="pl-4 pr-4">
            {t('cookieBar.SaveChoices')}
            <img src={arowRight} alt="" className="fix-arrow-cookiebar" />
          </span>
        }
        closeBtnClassName="cookie-bar-modal__cancel"
        footerClassName="cookie-bar-modal__footer"
        size="lg"
        onSubmitHandle={onSubmitCookiesForm}
        ref={modalRef}
        showHeader={false}
      >
        {({ closeModal }) => (
          <div className="cookie-bar-modal__panel">
            <div className="cookie-bar-modal__header">
              <div className="cookie-bar-modal__header-content">
                <div className="cookie-bar-modal__title_container">
                  <img
                    className="cookie-bar-mobile__icon"
                    src={cookieIcon}
                    alt=""
                    aria-hidden="true"
                  />
                  <h3 className="cookie-bar-modal__title">
                    {t('cookieBar.Title')}
                  </h3>
                </div>
                <p className="cookie-bar-modal__lead">
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('cookieBar.ModalText1'),
                    }}
                  />{' '}
                  <TranslatedLink lang={lang} to={slug}>
                    {t('cookieBar.ModalText2')}
                  </TranslatedLink>
                  .
                </p>
              </div>
              <button
                type="button"
                className="cookie-bar-modal__close"
                onClick={closeModal}
                aria-label={t('cookieBar.Cancel')}
              >
                ×
              </button>
            </div>

            <div className="cookie-bar-modal__body">
              <CookieToggle
                checked={true}
                disabled={true}
                sectionLabel={t('cookieBar.NesesseryTitle')}
                title={necessaryCopy.title}
                description={necessaryCopy.description}
                status={t('cookieBar.AlwaysActive')}
              />
              <CookieToggle
                checked={tagManagerValue}
                onChange={setTagManagerValue}
                sectionLabel={t('cookieBar.OptionalTitle')}
                title={optionalCopy.title}
                description={optionalCopy.description}
              />
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}

export default CookieBar
